import { GetLoansReturn } from 'graphql/loans/queries';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import querys from 'services/querys';
import useAlert from 'utils/alert';
import { capitalize } from 'utils/helpers';

export const LENDER_KIND = 'Lender';

type Parties = GetLoansReturn['getOrder']['parties'];

export const useLendersSection = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const queryCache = useQueryClient();
  const showAlert = useAlert();
  const { t } = useTranslation();
  const addInFlight = useRef(false);

  const updatePartiesCache = (parties: Parties) => {
    const cachedLoans = queryCache.getQueryData<GetLoansReturn>(['loans', orderId]);
    if (cachedLoans) {
      queryCache.setQueryData<GetLoansReturn>(['loans', orderId], {
        ...cachedLoans,
        getOrder: { ...cachedLoans.getOrder, parties },
      });
    }
    queryCache.invalidateQueries(['party-order', orderId]);
    queryCache.invalidateQueries(['order-parties', orderId]);
  };

  const { mutate: createLender, isLoading: isAdding } = useMutation(querys.createAssociation, {
    onSuccess: (data) => {
      updatePartiesCache(data.createOrderAssociation.parties);
      showAlert(capitalize(t('dialogs:order-updated')), 'success');
    },
  });

  const { mutate: deleteLender, isLoading: isRemoving, variables: removeVariables } = useMutation(
    querys.deleteAssociation,
    {
      onSuccess: (data) => {
        updatePartiesCache(data.deleteOrderAssociation.parties);
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
      },
    }
  );

  // Ref guard: state-based checks (isAdding) can be stale across two clicks in one render.
  const handleAddLender = (partyId: string, onSuccess: () => void) => {
    if (addInFlight.current) return;
    addInFlight.current = true;
    createLender(
      { id: orderId!, partyId, kind: LENDER_KIND, linkedToId: '', linkedToKind: '' },
      {
        onSuccess,
        onSettled: () => {
          addInFlight.current = false;
        },
      }
    );
  };

  const handleRemoveLender = (partyId: string) => {
    deleteLender({ id: orderId!, partyId, kind: LENDER_KIND });
  };

  return {
    isAdding,
    isRemoving,
    removingPartyId: removeVariables?.partyId,
    handleAddLender,
    handleRemoveLender,
  };
};
